import * as THREE from 'three';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import handlers from './handlers.js';
import tools from '../tools/tools.js';
import React, {useMemo, useEffect, useState, useRef} from 'react'
import * as ReactDOM from 'react-dom/client';
import {FxContext, SizeContext, CanvasFx} from './fx.js'

THREE.ColorManagement.enabled = false;

function Fxs(script, builtFun){
	let isAllReady = false
	let fxId = 0
	window.x40Fxs = []
	function allReady(){
		if(isAllReady){
			return
		}else{
			isAllReady = true
			if(builtFun){
				builtFun();
			}
			if(script.isTheme){
				document.documentElement.classList.add('glowz-built');
			}
		}
	}
	const readyQueue = new Set()
	function addToReadyQueue(fx){
		const id = {}
		readyQueue.add(id)
		fx.ready = ()=>{
			readyQueue.delete(id)
			if(readyQueue.size === 0){
				allReady()
			}
		}
		fx.isReady = ()=> {return readyQueue.size === 0}
		fx.isAllReady = ()=> {return isAllReady}
	}
	const roots = new Map();
	const rootFxSets = new Map();
	function mount(dom){
		if(script.simplifiedFx){
			return;
		}
		const s = dom.parentNode.parentNode;
		const fndPreview = (e)=>{
			if(e && e.parentNode && e.parentNode.classList && e.parentNode.classList.contains(script.isPreviewClass)){
				return true
			}else if(!e || e.parentNode === document.body){
				return false
			}else{
				return fndPreview(e.parentNode)
			}
		}
		if(fndPreview(s)){
			return
		}
		const isEditor = !document.querySelector('body.glowz-theme');
		const opt = ()=> {
			const res = JSON.parse(s.getAttribute('data-glowz-fx'));
			if(tools.isPhoton()){
				for (var prop in res) {
					if(prop.substring(0,3) === 'img' && prop.indexOf('-')===-1){
						res[prop] = tools.photonUrl(res[prop]);
					}
				}
			}
			return res;
		}
		const ext = {
			id: fxId,
			data: {},
			script: script,
			section: s,
			sectionFx: s.querySelector(':scope>.glowz-fx'),
			content: s.querySelector(':scope>.glowz-section-content'),
			dom,
			mouse: {x: 0.5, y: 0.5},
			swiperSlide: false,
			swiperHide: false,
		}
		fxId++
		if(dom.classList.contains('glowz-in-swiper')){
			const fnd = (e)=>{
				if(e.parentNode.classList.contains('swiper-slide') || e.parentNode === document.body){
					return e.parentNode
				}else{
					return fnd(e.parentNode)
				}
			}
			ext.swiperSlide = fnd(dom)
		}
		addToReadyQueue(ext)
		function rnd(rFx){
			const handler = handlers(rFx.effect);
			const config = handler.config ? {...handler.config} : {}
			const Effect = handler.App ? handler.App : handler
			function App(){
				const [fx, setFx] = useState(false)
				const [cFx, setCFx] = useState(false)
				const [size, setSize] = useState(false)
				const [rootFx, setRootFx] = useState(rFx)
				const sp = useRef(0);
				const spy = useRef(0);
				const calcTime = useRef(0);
				const fadeOut = useRef(0);
				const moveTime = useRef(0);
				useEffect(()=>{
					return ()=>{
						ext.ready()
					}
				},[])
				useEffect(()=>{
					rootFxSets.set(dom, setRootFx)
				},[setRootFx])
				useEffect(()=>{
					const fx = {}
					const canvas = document.createElement("canvas");
					dom.appendChild(canvas);
					fx.gl = new THREE.WebGLRenderer({
						canvas,
						antialias: (rootFx.antialiasing ? true : false) && !script.noAntiAlias,
						...config.gl
					})
					fx.clock = new THREE.Clock()
					if(config.createCamera){
						fx.camera = config.createCamera()
					}else{
						fx.camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000)
						fx.camera.position.z = 5
						fx.camera.lookAt(0, 0, 0)
					}
					fx.scene = new THREE.Scene()
					fx.scene.add( fx.camera )
					setCFx(fx)
					return ()=> {
						canvas.remove()
						fx.gl.dispose();
					}
				},[rootFx.antialiasing])
				useEffect(()=>{
					if(rootFx && cFx){
						const fx = {...rootFx, ...cFx}
						if(fx.id === 0){
							window.x40MainFx = fx
						}
						const defRender = ()=>{
							fx.gl.render(fx.scene, fx.camera)
						}
						let isAutoPaused = !rootFx.noPaused
						fx.setAutoPaused = (v)=>{
							isAutoPaused = v
							if(!isAutoPaused){
								fadeOut.current = 0
							}
						}
						fx.getAutoPaused = ()=>{
							return isAutoPaused;
						}
						const normalRunTimeFirst = 6
						const normalRunTimeNext = 3
						let normalRunTime = normalRunTimeFirst
						const stopTime = 1
						const defRun = ()=>{
							if(isAutoPaused){
								const afterMoveTime = calcTime.current - moveTime.current
								if(afterMoveTime <= normalRunTime){
									calcTime.current += fx.clock.getDelta()
									fadeOut.current = 0
								}else{
									const stopingTime = afterMoveTime - normalRunTime
									const stoping = stopingTime/stopTime
									fadeOut.current = stoping*stoping*stoping
									calcTime.current += fx.clock.getDelta()/(1 + 20*(fadeOut.current))
									if(stoping >= 1){
										normalRunTime = normalRunTimeNext
										fx.setIsAnimate(false)
									}
								}
							}else{
								calcTime.current += fx.clock.getDelta()
							}
							fx.frameSet.forEach((f)=>f(calcTime.current, fadeOut.current))
							fx.render();
						}
						fx.move = ()=>{
							if(isAutoPaused){
								moveTime.current = calcTime.current
								fx.setIsAnimate(true)
							}
						}
						fx.frameSet = new Set()
						fx.frameRun = (f)=>{
							fx.frameSet.add(f)
							return ()=>{
								fx.frameSet.delete(f)
							}
						}
						fx.render = defRender
						fx.defRender = defRender
						fx.setRender = (r)=>{
							if(r){
								fx.render = r
							}else if(r === false){
								fx.render = ()=>{}
							}else{
								fx.render = defRender
							}
						}
						fx.run = defRun
						const resizeB = () => {
							fx.sectionPos = fx.sectionFx.getBoundingClientRect().top + window.scrollY
							fx.visibleStart = fx.sectionPos -  window.innerHeight + 1;
							fx.visibleEnd = fx.sectionFx.getBoundingClientRect().bottom + window.scrollY - 1
						}
						const resizeObserverB = new ResizeObserver(resizeB);
						resizeObserverB.observe(document.querySelector('body'));
						resizeB();
						fx.isScrollOut = ()=>{
							return window.scrollY > (fx.sectionPos+1) ||
								fx.sectionPos - window.innerHeight + 1 > window.scrollY
						}
						const resizeC = ()=>{
							const domRect = dom.getBoundingClientRect();
							const width = domRect.width
							const height = domRect.height
							const dpr = (window.devicePixelRatio < 1 || script.isSsMode) ? window.devicePixelRatio : 1
							const w = Math.round(width*dpr)
							const h = Math.round(height*dpr)
							fx.gl.setSize(w, h)
							if(fx.camera.aspect !== undefined){
								fx.camera.aspect = width / height;
								fx.camera.updateProjectionMatrix()
								fx.camera.updateMatrixWorld()
							}
							fx.scrollProgress = () => {
								const scrollY = window.scrollY;
								if(scrollY != spy.current){
									spy.current = scrollY;
									let progressStartScrollY = fx.sectionPos - window.innerHeight;
									let scrollProgress;
									if(progressStartScrollY < 0){
										progressStartScrollY = 0;
									}
									if(scrollY >  progressStartScrollY){
										const progressEndScrollY = fx.sectionPos + height;
										scrollProgress = (scrollY - progressStartScrollY) / (progressEndScrollY - progressStartScrollY);
										if (scrollProgress > 1){
											scrollProgress = 1;
										}
									}else{
										scrollProgress = 0;
									}
									sp.current = scrollProgress;
								}
								return sp.current
							}
							setSize({width: w, height: h, dpr})
						}
						resizeC()
						const resizeObserverC = new ResizeObserver(resizeC);
						resizeObserverC.observe(dom);
						let isAnimate
						let isDispose = false
						let needRun
						if(fx.swiperSlide){
							fx.swiperHide = true
							const swiperState = ()=>{
								if(fx.swiperSlide.classList.contains('swiper-slide-active')){
									fx.swiperHide = false
								}else{
									fx.swiperHide = true
								}
							}
							const attrObserver = new MutationObserver((mutations) => {
								mutations.forEach(mu => {
									if (mu.type !== "attributes" && mu.attributeName !== "class") return;
									swiperState()
								});
							});
							attrObserver.observe(fx.swiperSlide, {attributes: true})
							swiperState()
						}
						if(isEditor){
							needRun = ()=>{
								const rec = fx.section.getBoundingClientRect()
								return rec.top < window.innerHeight && rec.bottom > 0 && !fx.swiperHide
							}
						}else if(script.isSsMode){
							needRun = ()=>{
								if(script.ssInScroll){
									resizeB()
								}
								return window.scrollY > fx.visibleStart && window.scrollY < fx.visibleEnd && !fx.swiperHide
							}	
						}else{
							needRun = ()=>{
								return window.scrollY > fx.visibleStart && window.scrollY < fx.visibleEnd && !fx.swiperHide
							}
						}
						const animate = ()=> {
							if(script.isFxStop){
								script.fxRestore.add(()=>{
									fx.clock.start()
									animate()
								})
								fx.clock.stop()
							}else if(isAnimate && !isDispose){
								requestAnimationFrame(animate)
								if(fx.isAllReady()){
									if(needRun()){
										fx.run()
									}
								}
							}
						}
						fx.setIsAnimate = (isRun=true)=>{
							if(isRun && !isAnimate){
								isAnimate = isRun
								fx.clock.start()
								animate()
							}else if(!isRun && isAnimate){
								isAnimate = isRun
								fx.clock.stop()
							}else{
								isAnimate = isRun
							}
						}
						fx.setIsAnimate(true)
						window.x40Fxs.push(fx)
						setFx(fx)
						return ()=> {
							isDispose = true
							resizeObserverB.disconnect()
							resizeObserverC.disconnect()
						}
					}
				},[cFx, rootFx])
				return (
					<>
					{fx && size && (
						<FxContext.Provider value={fx}>
							<SizeContext.Provider value={size}>
								<CanvasFx>
									<Effect />
								</CanvasFx>
							</SizeContext.Provider>
						</FxContext.Provider>
					) }
					</>
				)
			}
			const eRoot = roots.get(dom)
			let root
			if(eRoot){
				root = eRoot
			}else{
				root = ReactDOM.createRoot(dom)
				roots.set(dom, root)
			}
			root.render(<App />);
		}
		let currentFx = {...opt(),	...ext}
		const dataObserver = new MutationObserver(function(mutations) {
			if(dom.getAttribute('data-glowz-effect-mounted')){
				currentFx = {...opt(),	...ext}
				const rfs = rootFxSets.get(dom)
				if(rfs){
					rfs(currentFx)
				}else{
					rnd(currentFx)
				}
			}
		});
		dataObserver.observe(s, { attributes : true, attributeFilter : ['data-glowz-fx'] });
		dom.addEventListener('glowz-unmount-effect', function(){
			dataObserver.disconnect();
		}, tools.passiveListener)
		rnd(currentFx)
		dom.setAttribute('data-glowz-effect-mounted', '1')
		return currentFx
	}
	function unmount(dom){
		dom.dispatchEvent(new Event('glowz-unmount-effect'))
		if(dom.getAttribute('data-glowz-effect-mounted')){
			dom.setAttribute('data-glowz-effect-mounted', '');
			roots.get(dom).unmount()
			roots.delete(dom)
			rootFxSets.delete(dom)
		}
	}
	window.glowz.fxs = {mount, unmount}
	this.ready = function(){
		if(script.simplifiedFx){
			allReady();
			return;
		}else if(!tools.isWebGL2Available()){
			script.simplifiedFxOn()
			allReady();
			return;
		}
		document.querySelectorAll('.swiper-slide .glowz-section-effect').forEach((el)=>{
			el.classList.add('glowz-in-swiper');
		})
		document.querySelectorAll('.glowz-loading-overlay .glowz-set-effect, .glowz-loading-indicator  .glowz-set-effect').forEach((el)=>{
			el.classList.add('glowz-effect-no');
		})
		const isNeedEffLoop = document.querySelector('.wp-site-blocks');
		if(isNeedEffLoop){
			const effs = document.querySelectorAll('.glowz-set-effect:not(.glowz-effect-no)');
			if(script.isTheme && effs.length < 1){
				allReady();
			}else{
				effs.forEach(function(s, i){
					const fx = mount(s.querySelector(':scope>.glowz-fx>.glowz-section-effect'))
				});
				if(readyQueue.size === 0){
					allReady();
				}
			}
		}
	}
}
export default Fxs;
